import {Action, Attributes, AttributesRow, Card, DataStatus, openDialog} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {useReadCodelistQuery} from '@dms/api/core';
import {useGetVehicleFinancialInstitutionQuery} from '@dms/api/sales';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, VehicleFinancingCard} from '@dms/shared';

import {buildArray, isApiDateString, parseDate, suffixTestId, TestIdProps} from 'shared/web';

interface VehicleFinancialInstitutionProps extends TestIdProps {
  vehicleId: string;
  canEditVehicle: boolean;
}

const LEASING_CODELIST_ID = 'leasing_and_credit_company';

export function VehicleFinancialInstitution(props: VehicleFinancialInstitutionProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data, isLoading, isError} = useGetVehicleFinancialInstitutionQuery({
    vehicleId: props.vehicleId,
  });

  const {data: saleVehicle} = useGetSaleVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNil(props.vehicleId)}
  );

  const {data: leasingAndCreditCompanies} = useReadCodelistQuery({
    codelistId: LEASING_CODELIST_ID,
  });

  const institution = leasingAndCreditCompanies?.codes.find(
    (code) => code.codeId === saleVehicle?.leasingAndCreditCompanyCodeId
  )?.name;

  const attributeRows = buildArray<AttributesRow>()
    .add({
      label: i18n.t('entity.vehicleFinancialInstitution.labels.financed'),
      value: saleVehicle?.leasingAndCreditCompany
        ? i18n.t('general.labels.yes')
        : i18n.t('general.labels.no'),
    })
    .when(saleVehicle?.leasingAndCreditCompany, {
      label: i18n.t('entity.vehicleFinancialInstitution.labels.institution'),
      value: institution,
    })
    .when(saleVehicle?.leasingAndCreditCompany, {
      label: i18n.t('entity.checkout.labels.endOfFinancing'),
      value: isApiDateString(saleVehicle?.endOfFinancing)
        ? formatDateTime('dateShort', parseDate(saleVehicle!.endOfFinancing))
        : EMPTY_PLACEHOLDER,
    })
    .when(saleVehicle?.leasingAndCreditCompany, {
      label: i18n.t('entity.checkout.labels.contractNumber'),
      value: saleVehicle?.financingContractNumber ?? EMPTY_PLACEHOLDER,
    })
    .when(saleVehicle?.leasingAndCreditCompany, {
      label: i18n.t('general.labels.note'),
      value: saleVehicle?.financingNote ?? EMPTY_PLACEHOLDER,
    });

  const cardActions = buildArray<Action>().add({
    type: 'button',
    variant: 'link',
    leftIcon: 'image/edit',
    title: i18n.t('general.actions.edit'),
    isDisabled: isNil(data),
    onClick: () =>
      openDialog(
        <VehicleFinancingCard
          vehicleId={props.vehicleId}
          isWithSubmit
          data-testid={suffixTestId('financingCard', props)}
        />,
        {
          title: i18n.t('entity.vehicleFinancialInstitution.labels.title'),
          scrollBehavior: 'outside',
        }
      ),
  });

  return (
    <Card
      title={i18n.t('entity.vehicleFinancialInstitution.labels.title')}
      actions={props.canEditVehicle ? cardActions : undefined}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Attributes rows={attributeRows} data-testid={suffixTestId('attributes', props)} />
      </DataStatus>
    </Card>
  );
}
